import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Apple,
  Smartphone,
  Bell,
  Heart,
  BookOpen,
  Flame,
  Users,
  Zap,
} from "lucide-react";
import useLenis from "@/hooks/useLenis";
import useSEO from "@/hooks/useSEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";

const FEATURES = [
  {
    Icon: BookOpen,
    title: "Daily Bread Tracker",
    body: "Log Scripture, reflections, and personal-development chapters. Your spiritual journal, always with you.",
  },
  {
    Icon: Flame,
    title: "Turn 180 Command",
    body: "Weekly Fat & Nasty challenges, streaks, and the covenant reminder — every morning at dawn.",
  },
  {
    Icon: Heart,
    title: "Session Logger",
    body: "Lifts, cardio, mobility. Zone-2 heart-rate, PRs, and a private history that spans the 180 days.",
  },
  {
    Icon: Users,
    title: "Ultimate Warrior Feed",
    body: "Post your proof. Follow the numbered. No likes, no algorithm — just accountability, sealed.",
  },
  {
    Icon: Zap,
    title: "Program Delivery",
    body: "Ascent · Altitude · Stillness · Iron Order — every rep, every rest, every read, in one place.",
  },
  {
    Icon: Bell,
    title: "Silent Dispatch",
    body: "One quiet notification per day. Never a leaderboard, never a badge. The work speaks.",
  },
];

const NotifyForm = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [numberedId, setNumberedId] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error("Enter a valid email.");
      return;
    }
    setStatus("loading");
    try {
      const res = await api.post("/newsletter", { email: email.trim() });
      setNumberedId(res.data?.numbered_id || "");
      setStatus("success");
      toast.success("You're on the app launch list.");
    } catch (err) {
      setStatus("idle");
      toast.error(formatApiError(err));
    }
  };

  if (status === "success") {
    return (
      <div className="border border-silver-100/40 rounded-md p-8 bg-white/[0.03]">
        <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
          §— ON THE LIST
        </div>
        <div className="font-serif italic text-3xl md:text-4xl text-silver-100 leading-[1.05]">
          You&apos;ll get the app first.
        </div>
        <div className="mt-4 font-mono text-[11px] tracking-[0.36em] text-white">
          YOUR NUMBER — {numberedId}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="mbsapp-notify-form"
      className="border border-white/10 rounded-md p-8 md:p-10 bg-white/[0.02]"
    >
      <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
        §— NOTIFY ME
      </div>
      <h3 className="font-serif text-white text-3xl md:text-4xl leading-[1.02]">
        Get the app <span className="italic text-silver-100">before it opens.</span>
      </h3>
      <p className="text-white/55 text-[14px] leading-[1.7] mt-3">
        Enlist and you&apos;ll be first in the door on launch day, with a founding-member
        number and a lifetime lock on your rate.
      </p>

      <label className="block mt-6">
        <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">
          EMAIL
        </span>
        <input
          type="email"
          required
          data-testid="mbsapp-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
          placeholder="you@dispatch.com"
        />
      </label>

      <button
        type="submit"
        data-testid="mbsapp-notify-submit"
        disabled={status === "loading"}
        className="mt-8 inline-flex items-center gap-3 px-6 py-4 rounded-full bg-silver-100 text-black hover:bg-white transition-colors duration-500 disabled:opacity-60"
      >
        <span className="text-[11px] tracking-[0.32em] uppercase font-medium">
          {status === "loading" ? "Sealing…" : "Notify Me at Launch"}
        </span>
      </button>
    </form>
  );
};

const MBSApp = () => {
  useLenis();
  useSEO({
    title: "MBS Fitness App — Coming Soon · Mind. Body. Soul.",
    description:
      "The MBS Fitness App is coming soon — Turn 180 tracker, program delivery, session logger, and Ultimate Warrior feed. Enlist to get notified at launch.",
  });

  return (
    <div className="relative bg-black text-white min-h-screen">
      <Header />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden pt-32 md:pt-44 pb-24 md:pb-28">
          {/* Backdrop */}
          <div className="absolute inset-0 flex items-center justify-center opacity-[0.045] pointer-events-none">
            <Logo showWordmark={false} className="w-[90vw] max-w-[1400px] h-auto" />
          </div>
          <div className="absolute inset-0 grain-overlay opacity-[0.08] pointer-events-none" />
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[1100px] rounded-full bg-[radial-gradient(circle,rgba(229,228,226,0.06)_0%,transparent_60%)]" />
          </div>

          <div className="relative max-w-[1500px] mx-auto px-6 md:px-10">
            <Link
              to="/"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/50 hover:text-white transition-colors mb-10"
            >
              <span className="w-6 h-px bg-silver-100" />
              Back to home
            </Link>

            <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
              <div className="col-span-12 md:col-span-8">
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                    §THE APP
                  </span>
                  <span className="inline-flex items-center gap-2 px-2.5 py-1 border border-silver-100/40 rounded-full font-mono text-[9px] tracking-[0.32em] uppercase text-silver-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-silver-100 animate-pulse" />
                    Coming Soon
                  </span>
                </div>

                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="font-serif text-white leading-[0.86] tracking-[-0.03em] text-[20vw] md:text-[13vw] lg:text-[11vw]"
                >
                  MBS
                  <br />
                  <span className="italic text-silver-100">Fitness App.</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.3 }}
                  className="mt-8 font-serif italic text-silver-100 text-2xl md:text-3xl leading-[1.15] max-w-2xl"
                >
                  The training journal, the covenant, and the order — in your
                  pocket.
                </motion.p>
              </div>

              {/* Phone mockup */}
              <motion.div
                initial={{ opacity: 0, x: 40, rotate: -5 }}
                animate={{ opacity: 1, x: 0, rotate: -3 }}
                transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-12 md:col-span-4 flex justify-center md:justify-end"
              >
                <div className="relative w-[240px] md:w-[280px] aspect-[9/19] rounded-[42px] bg-gradient-to-b from-neutral-800 to-black border border-white/10 shadow-[0_40px_120px_-20px_rgba(229,228,226,0.25)] p-3">
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-30" />
                  <div className="relative w-full h-full rounded-[32px] overflow-hidden bg-black">
                    <div className="absolute inset-x-0 top-0 p-6 pt-14 z-20">
                      <div className="font-mono text-[9px] tracking-[0.35em] text-silver-300">
                        DAY 042 · TURN 180
                      </div>
                      <div className="mt-3 font-serif text-white text-2xl leading-tight">
                        Zone 2
                        <br />
                        <span className="italic text-silver-200">for 46:00</span>
                      </div>
                    </div>

                    {/* Ring */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative w-36 h-36">
                        <div className="absolute inset-0 rounded-full border border-white/10" />
                        <motion.div
                          className="absolute inset-0 rounded-full"
                          style={{
                            background:
                              "conic-gradient(from -90deg, #E5E4E2 0deg, #E5E4E2 260deg, transparent 260deg)",
                            WebkitMask:
                              "radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 5px))",
                            mask:
                              "radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 5px))",
                          }}
                          animate={{ rotate: [0, 360] }}
                          transition={{ repeat: Infinity, duration: 12, ease: "linear" }}
                        />
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <div className="font-mono text-[9px] tracking-[0.3em] text-silver-300">
                            BPM
                          </div>
                          <div className="font-serif text-white text-4xl leading-none mt-1">
                            142
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="absolute inset-x-0 bottom-0 p-6 z-20">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        {[["06:12", "PACE"], ["3.4mi", "DIST"], ["82%", "READY"]].map(([v, l]) => (
                          <div key={l} className="border-t border-white/10 pt-3 text-white">
                            <div className="font-mono text-sm">{v}</div>
                            <div className="font-mono text-[8px] tracking-[0.3em] text-white/50 mt-1">
                              {l}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,rgba(229,228,226,0.12),transparent_60%)]" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Availability strip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="mt-20 md:mt-24 grid grid-cols-2 md:grid-cols-3 gap-6 border-t border-white/10 pt-10"
            >
              <div>
                <div className="flex items-center gap-2 text-white/70">
                  <Apple className="h-5 w-5" strokeWidth={1.5} />
                  <div className="font-mono text-[10px] tracking-[0.32em] uppercase">
                    iOS
                  </div>
                </div>
                <div className="mt-2 font-serif italic text-silver-100 text-xl">
                  Coming Soon.
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-white/70">
                  <Smartphone className="h-5 w-5" strokeWidth={1.5} />
                  <div className="font-mono text-[10px] tracking-[0.32em] uppercase">
                    Android
                  </div>
                </div>
                <div className="mt-2 font-serif italic text-silver-100 text-xl">
                  Coming Soon.
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <div className="font-mono text-[10px] tracking-[0.32em] text-silver-300 uppercase">
                  Founding members
                </div>
                <div className="mt-2 font-serif text-white text-xl leading-tight">
                  Locked at first price,{" "}
                  <span className="italic text-silver-100">forever.</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="relative py-24 md:py-32 border-t border-white/10 bg-black">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9 }}
              className="flex items-end justify-between mb-16 md:mb-20 border-b border-white/10 pb-8"
            >
              <div>
                <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
                  §01 — FEATURES
                </div>
                <h2 className="font-serif text-[10vw] md:text-[5vw] leading-[0.95] tracking-[-0.02em]">
                  Six chapters
                  <br />
                  <span className="italic text-silver-100">of the work.</span>
                </h2>
              </div>
              <div className="hidden md:block font-mono text-[10px] tracking-[0.32em] text-white/40">
                01 — 06
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/8">
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.06 }}
                  className="group bg-black p-8 md:p-10 min-h-[280px] flex flex-col justify-between hover:bg-white/[0.02] transition-colors"
                >
                  <div>
                    <div className="w-12 h-12 rounded-full border border-silver-100/30 flex items-center justify-center mb-6 group-hover:border-silver-100/60 transition-colors">
                      <f.Icon className="h-5 w-5 text-silver-100" strokeWidth={1.3} />
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.35em] text-silver-300 mb-3">
                      0{i + 1}
                    </div>
                    <h3 className="font-serif text-white text-2xl md:text-3xl leading-[1.05]">
                      {f.title}
                    </h3>
                  </div>
                  <p className="mt-6 text-white/60 text-[14px] leading-[1.75]">
                    {f.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Notify */}
        <section
          id="notify"
          className="relative py-24 md:py-32 border-y border-white/10"
        >
          <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-14 items-start">
            <div className="col-span-12 md:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
              >
                <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
                  §02 — LAUNCH LIST
                </div>
                <h2 className="font-serif text-white text-[10vw] md:text-[5vw] leading-[0.95] tracking-[-0.02em]">
                  Get it{" "}
                  <span className="italic text-silver-100">first.</span>
                </h2>
                <p className="mt-6 text-white/60 leading-[1.85] max-w-md text-[15px]">
                  When the app opens, the order goes in first. Enlist your
                  email and we&apos;ll seal your founding-member number the
                  moment iOS + Android drop.
                </p>
                <div className="mt-8 pt-8 border-t border-white/10 flex items-center gap-4">
                  <Logo showWordmark={false} className="h-10 w-10" />
                  <div className="font-mono text-[10px] tracking-[0.4em] text-silver-300">
                    MBS · MIND. BODY. SOUL.
                  </div>
                </div>
              </motion.div>
            </div>
            <div className="col-span-12 md:col-span-7">
              <NotifyForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MBSApp;
